import { ImpersonationBanner } from '@/components/ImpersonationBanner';
import { Header } from '@/components/layout/Header';
import { useSincronizarCumplimiento } from '@/hooks/useSysadmin';
import {
  BarChart3,
  Building2,
  ChevronRight,
  ClipboardList,
  Copy,
  Database,
  FileText,
  FileWarning,
  FolderSearch,
  FileAudio,
  FolderSync,
  Home,
  Link2,
  ListChecks,
  Mic,
  PanelLeftClose,
  PanelLeftOpen,
  RefreshCw,
  Server,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  Users,
  Wrench,
} from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

// ─── Navigation Config ───────────────────────────────────────────

interface NavItem {
  path: string;
  label: string;
  icon: ReactNode;
  description: string;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Gestión',
    items: [
      {
        path: 'usuarios',
        label: 'Usuarios',
        icon: <Users className="w-4.5 h-4.5" />,
        description: 'Gestiona asesores y roles del sistema',
      },
      {
        path: 'empresas',
        label: 'Empresas',
        icon: <Building2 className="w-4.5 h-4.5" />,
        description: 'Administra empresas y asignaciones',
      },
    ],
  },
  {
    title: 'Normativa',
    items: [
      {
        path: 'requisitos',
        label: 'Requisitos',
        icon: <ClipboardList className="w-4.5 h-4.5" />,
        description: 'Catálogo y asociación de requisitos',
      },
      {
        path: 'asociaciones',
        label: 'Asociaciones',
        icon: <Link2 className="w-4.5 h-4.5" />,
        description: 'Relaciones entre requisitos de NTCs',
      },
      {
        path: 'indicadores',
        label: 'Indicadores',
        icon: <BarChart3 className="w-4.5 h-4.5" />,
        description: 'Tipos y metas de indicadores de calidad',
      },
    ],
  },
  {
    title: 'Monitoreo',
    items: [
      {
        path: 'auditoria',
        label: 'Auditoría',
        icon: <ShieldCheck className="w-4.5 h-4.5" />,
        description: 'Historial de asesorías por empresa',
      },
      {
        path: 'evidencias',
        label: 'Evidencias',
        icon: <FileText className="w-4.5 h-4.5" />,
        description: 'Documentos subidos al sistema',
      },
      {
        path: 'asesorias',
        label: 'Asesorías',
        icon: <ClipboardList className="w-4.5 h-4.5" />,
        description: 'Asesorías de todas las empresas',
      },
      {
        path: 'actas',
        label: 'Actas faltantes',
        icon: <FileWarning className="w-4.5 h-4.5" />,
        description: 'Asesorías bloqueadas por falta de acta',
      },
      {
        path: 'anomalias',
        label: 'Anomalías de estado',
        icon: <ShieldAlert className="w-4.5 h-4.5" />,
        description: 'Empresas con asesorías en estados que ningún flujo espera',
      },
      {
        path: 'grabaciones',
        label: 'Grabaciones',
        icon: <Mic className="w-4.5 h-4.5" />,
        description: 'Audios de sesiones de asesoría',
      },
      {
        path: 'auditoria-drive',
        label: 'Auditoría de Drive',
        icon: <FolderSearch className="w-4.5 h-4.5" />,
        description: 'Verifica borrados reales y archivos huérfanos en Google Drive',
      },
      {
        path: 'logs',
        label: 'Logs del Sistema',
        icon: <Server className="w-4.5 h-4.5" />,
        description: 'Tareas en segundo plano y webhooks',
      },
      {
        path: 'limpieza-logs',
        label: 'Limpieza de logs',
        icon: <Trash2 className="w-4.5 h-4.5" />,
        description: 'Filtra los logs guardados en MongoDB y elimina los que ya no sirven',
      },
      {
        path: 'diagnostico-ntc',
        label: 'Diagnóstico NTC',
        icon: <ShieldAlert className="w-4.5 h-4.5" />,
        description: 'Empresas con anomalías en registros NTC',
      },
    ],
  },
  {
    title: 'Herramientas',
    items: [
      {
        path: 'drive',
        label: 'Google Drive',
        icon: <FolderSync className="w-4.5 h-4.5" />,
        description: 'Copiar y compartir archivos de Drive',
      },
      {
        path: 'database',
        label: 'Base de Datos',
        icon: <Database className="w-4.5 h-4.5" />,
        description: 'Explorador de esquemas y registros',
      },
      {
        path: 'regenerar-actas',
        label: 'Regenerar Actas',
        icon: <RefreshCw className="w-4.5 h-4.5" />,
        description: 'Regenera actas con el formato nuevo, reusando datos existentes',
      },
      {
        path: 'prueba-acta-audios',
        label: 'Prueba Acta (Audios)',
        icon: <FileAudio className="w-4.5 h-4.5" />,
        description: 'Copia un acta real y le agrega la fila de audios para revisarla',
      },
      {
        path: 'correcciones-asesorias',
        label: 'Correcciones de Asesorías',
        icon: <Wrench className="w-4.5 h-4.5" />,
        description: 'Audios en la asesoría equivocada, cambios de dueño y asesorías sin audios',
      },
      {
        path: 'pendientes-actas',
        label: 'Actas con hallazgos',
        icon: <ListChecks className="w-4.5 h-4.5" />,
        description: 'Seguimiento de las actas con problemas del diagnóstico',
      },
      {
        path: 'audios-ajenos',
        label: 'Audios de otro asesor',
        icon: <Users className="w-4.5 h-4.5" />,
        description: 'Decidir si se cambia el asesor o se separa la asesoría',
      },
      {
        path: 'audios-duplicados',
        label: 'Audios duplicados',
        icon: <Copy className="w-4.5 h-4.5" />,
        description: 'Audios con la misma duración en la misma empresa o del mismo asesor',
      },
    ],
  },
];

// Flat lookup for breadcrumb
const ALL_ITEMS = NAV_SECTIONS.flatMap((s) => s.items.map((item) => ({ ...item, sectionTitle: s.title })));

// ─── Breadcrumb ──────────────────────────────────────────────────

function Breadcrumb() {
  const location = useLocation();
  const segments = location.pathname
    .replace(/^\/admin\/?/, '')
    .split('/')
    .filter(Boolean);
  const activeKey = segments[0] || 'usuarios';
  const match = ALL_ITEMS.find((i) => i.path === activeKey);

  return (
    <nav className="flex items-center gap-1.5 text-sm text-gray-500 select-none" aria-label="Breadcrumb">
      <NavLink to="/admin" className="flex items-center gap-1 hover:text-gray-700 transition-colors">
        <Home className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Administración</span>
      </NavLink>
      {match && (
        <>
          <ChevronRight className="w-3 h-3 text-gray-300" />
          <span className="text-gray-400">{match.sectionTitle}</span>
          <ChevronRight className="w-3 h-3 text-gray-300" />
          <span className="text-gray-900 font-semibold">{match.label}</span>
        </>
      )}
    </nav>
  );
}

// ─── Sidebar Nav Item ────────────────────────────────────────────

function SidebarItem({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  return (
    <NavLink
      to={`/admin/${item.path}`}
      className={({ isActive }) =>
        `group flex items-center gap-3 rounded-lg transition-all duration-150 relative ${
          collapsed ? 'justify-center px-2 py-2.5 mx-1' : 'px-3 py-2.5 mx-2'
        } ${
          isActive
            ? 'bg-blue-50 text-blue-700 font-semibold shadow-sm shadow-blue-100/50'
            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
        }`
      }
    >
      {({ isActive }) => (
        <>
          {/* Active indicator bar */}
          {isActive && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.75 h-5 bg-blue-600 rounded-r-full" />}
          <span className={`shrink-0 transition-colors ${isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}`}>
            {item.icon}
          </span>
          {!collapsed && <span className="text-sm truncate">{item.label}</span>}

          {/* Tooltip when collapsed */}
          {collapsed && (
            <div className="absolute left-full ml-2 px-2.5 py-1.5 bg-gray-900 text-white text-xs font-medium rounded-md opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 shadow-lg">
              {item.label}
              <div className="absolute top-1/2 -translate-y-1/2 -left-1 w-2 h-2 bg-gray-900 rotate-45" />
            </div>
          )}
        </>
      )}
    </NavLink>
  );
}

// ─── Main Layout ─────────────────────────────────────────────────

export function SysadminLayout() {
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sysadmin-sidebar-collapsed');
    return saved === 'true';
  });
  const { isLoading: isSincronizando, sincronizar } = useSincronizarCumplimiento();

  const location = useLocation();
  const segments = location.pathname
    .replace(/^\/admin\/?/, '')
    .split('/')
    .filter(Boolean);
  const activeKey = segments[0] || 'usuarios';
  const activeItem = ALL_ITEMS.find((i) => i.path === activeKey);

  const toggleCollapse = () => {
    setCollapsed((prev) => {
      localStorage.setItem('sysadmin-sidebar-collapsed', String(!prev));
      return !prev;
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <ImpersonationBanner />
      <Header />

      <div className="flex flex-1 min-h-0">
        {/* ─── Sidebar ─── */}
        <aside
          className={`bg-white border-r border-gray-200 flex flex-col shrink-0 transition-all duration-300 ease-in-out ${
            collapsed ? 'w-17' : 'w-65'
          }`}
        >
          {/* Sidebar header */}
          <div
            className={`border-b border-gray-100 flex items-center ${collapsed ? 'justify-center px-2 py-4' : 'justify-between px-4 py-4'}`}
          >
            {!collapsed && (
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                  <Settings className="w-4 h-4 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-sm font-bold text-gray-900 truncate">Administración</h2>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Panel de control</p>
                </div>
              </div>
            )}
            <button
              onClick={toggleCollapse}
              className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors shrink-0"
              title={collapsed ? 'Expandir sidebar' : 'Colapsar sidebar'}
            >
              {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
            </button>
          </div>

          {/* Nav sections */}
          <nav className="flex-1 overflow-y-auto py-3 scrollbar-thin scrollbar-thumb-gray-200">
            {NAV_SECTIONS.map((section) => (
              <div key={section.title} className="mb-1">
                {!collapsed && (
                  <p className="px-5 pt-4 pb-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest select-none">
                    {section.title}
                  </p>
                )}
                {collapsed && <div className="mx-3 my-2 border-t border-gray-100" />}
                <div className="space-y-0.5">
                  {section.items.map((item) => (
                    <SidebarItem key={item.path} item={item} collapsed={collapsed} />
                  ))}
                </div>
              </div>
            ))}
          </nav>

          {/* Sidebar footer */}
          {!collapsed && (
            <div className="border-t border-gray-100 px-4 py-3">
              <p className="text-[10px] text-gray-300 text-center font-medium uppercase tracking-wider">Vivexia Sysadmin v2</p>
            </div>
          )}
        </aside>

        {/* ─── Content Area ─── */}
        <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Content header with breadcrumb */}
          <div className="bg-white border-b border-gray-100 px-6 py-4 shrink-0">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <Breadcrumb />
                {activeItem && (
                  <div className="mt-2">
                    <h1 className="text-xl font-bold text-gray-900">{activeItem.label}</h1>
                    <p className="text-sm text-gray-500 mt-0.5">{activeItem.description}</p>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={async () => {
                  const ok = window.confirm(
                    '¿Sincronizar historial de asesorías? Esto reconstruirá el estado actual de todas las empresas. Esta operación puede tardar.'
                  );
                  if (!ok) return;
                  try {
                    const processed = await sincronizar();
                    window.alert(`Sincronización completada. Empresas procesadas: ${processed}`);
                  } catch (err) {
                    window.alert(err instanceof Error ? err.message : 'Error al sincronizar historial');
                  }
                }}
                disabled={isSincronizando}
                className="shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Reconstruir el estado acumulado de todas las empresas desde el historial de asesorías"
              >
                <RefreshCw className={`w-4 h-4 ${isSincronizando ? 'animate-spin' : ''}`} />
                {isSincronizando ? 'Sincronizando…' : 'Sincronizar Historial'}
              </button>
            </div>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
